import { resolveTip, roundMoney, allocateGlobalTip } from 'shared-models/browser';
import type { OrderView } from '../../../../../services/orders/orders.service';
import type { PaymentlineView } from '../../../../../services/payments/payments.service';

export type PaymentScreenTipView = {
  resolvedGlobalTip: number;
  visiblePaymentlines: PaymentlineView[];
  openLineCount: number;
  openTipShareById: Map<number, number>;
  hasOpenLineTips: boolean;
};

export type ReceiptTip = { globalTip: number; lineTips: number; tip: number; paidTip: number };

// Single source for the receipt/summary tip total: global tip on the billable base + Σ live line tips.
// Also reports paidTip = tip already collected on done lines (frozen global share + their own tip), so
// "paid"/"remaining" account for the tip. Used by OrderSummary + the printed receipt (no drift).
export function deriveReceiptTip(
  order: OrderView | null | undefined,
  paymentlines: PaymentlineView[],
  billableTotal: number,
): ReceiptTip {
  const globalTip = resolveTip(order?.tipInputMode ?? null, order?.tipInputValue ?? null, billableTotal);
  const lineTips = paymentlines
    .filter((line) => (line.status === 'new' || line.status === 'done') && !line.canceledAt)
    .reduce((sum, line) => sum + resolveTip(line.tipInputMode ?? null, line.tipInputValue ?? null, line.amount), 0);
  const paidTip = paymentlines
    .filter((line) => line.status === 'done' && !line.canceledAt)
    .reduce(
      (sum, line) => sum + (line.globalTipCollected ?? 0) + resolveTip(line.tipInputMode ?? null, line.tipInputValue ?? null, line.amount),
      0,
    );
  return { globalTip, lineTips, tip: globalTip + lineTips, paidTip };
}

// Pure derivation of the payment-screen tip view: resolved global tip + each open line's proportional share
// (done lines keep their frozen globalTipCollected, so only the remaining global tip is split across open lines).
export function derivePaymentScreenTip(
  order: OrderView | null | undefined,
  billableTotal: number,
  paymentlines: PaymentlineView[],
): PaymentScreenTipView {
  const resolvedGlobalTip = order
    ? resolveTip(order.tipInputMode ?? null, order.tipInputValue ?? null, billableTotal)
    : 0;

  const visiblePaymentlines = paymentlines
    .filter((line) => line.status === 'new' || (line.status === 'done' && !line.canceledAt))
    // Paid ('done') lines render above pending ('new') lines; stable by id within each group.
    .sort((a, b) => (a.status === b.status ? a.id - b.id : a.status === 'done' ? -1 : 1));
  // Sort by id (asc) so the tip allocation order matches the backend freeze (deterministic even-total split).
  const openTipLines = visiblePaymentlines.filter((line) => line.status === 'new').sort((a, b) => a.id - b.id);

  const doneTipCollected = visiblePaymentlines
    .filter((line) => line.status === 'done' && !line.canceledAt)
    .reduce((sum, line) => sum + (line.globalTipCollected ?? 0), 0);

  const openTipShares = allocateGlobalTip(
    roundMoney(resolvedGlobalTip - doneTipCollected),
    openTipLines.map((line) => line.amount),
  );
  const openTipShareById = new Map(openTipLines.map((line, index) => [line.id, openTipShares[index] ?? 0]));

  const hasOpenLineTips = openTipLines.some(
    (line) => resolveTip(line.tipInputMode ?? null, line.tipInputValue ?? null, line.amount) > 0,
  );

  return {
    resolvedGlobalTip,
    visiblePaymentlines,
    openLineCount: openTipLines.length,
    openTipShareById,
    hasOpenLineTips,
  };
}
